import React, { BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  Card,
  DescriptionList,
  Heading,
  Icon,
  IconSize,
  Layout,
  Spinner,
  Tag,
} from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import { prepareDashboardIdent } from '../../../../../services/api/ident/prepareDashboardIdent';
import { startDashboardIdent } from '../../../../../services/api/ident/startIdent';
import { getCreditApplicationCustomerData } from '../../../../../services/api/miles/miles-credit-services';
import { getQuoteStakeholderContacts } from '../../../../../services/api/miles/miles-services';
import {
  getCoCustomerCreditScoreId,
  getCreditApplicationId,
  getCustomerCreditScoreId,
} from '../../../../../services/api/miles/milesIdProvider';
import { waitForMiles } from '../../../../../services/common/miscUtils';
import { useRemoveCardTitle } from '../../../../../services/common/useRemoveCardTitle';
import { storage } from '../../../../../services/localStorage';
import { loadingSelectors } from '../../../../../services/redux/features/loading.slice';
import {
  MilesCreditApplicationStipulationStatus,
  MilesStipulationTypeDefinitionId,
  StipulationItem,
} from '../../../../../types/dashboardDocuments';
import { ApplicantType } from '../../../../../types/identLandingPage';
import { extractDashboardIdentData } from '../../../../FormPage/Step4/utils';
import { DashboardActionPopup } from '../../../popups/DashboardPopups';
import {
  DashboardSubpageDocumentsEntityType,
  DashboardSubpageDocumentsSourceType,
} from '../../DashboardSubpageDocuments/DashboardSubpageDocuments';
import { useCentralDocumentHandling } from './useCentralDocumentHandling';

const isIdCardStipulation = (stipulation: StipulationItem): boolean =>
  stipulation.stipulationTypeDefinitionId === MilesStipulationTypeDefinitionId.ID_CARD_APPLICANT;

const isSignatoryIdCardStipulation = (stipulation: StipulationItem): boolean =>
  stipulation.stipulationTypeDefinitionId === MilesStipulationTypeDefinitionId.ID_CARD_SIGNATORY;

const isOnfidoRequired = (stipulation: StipulationItem): boolean =>
  (isIdCardStipulation(stipulation) || isSignatoryIdCardStipulation(stipulation)) &&
  stipulation.status === MilesCreditApplicationStipulationStatus.CREATED;

interface Props {
  id: string; // OfferId | pendingContractId
  sourceType: DashboardSubpageDocumentsSourceType;
  entityType: DashboardSubpageDocumentsEntityType;
}

export const DashboardDocumentUpload: FC<Props> = ({ id, sourceType, entityType }) => {
  const uploadInputs = useRef({});
  const {
    getStipulationList,
    stipulations,
    coApplicantStipulations,
    creditApplicationCoApplicantId,
    loadingId,
    getStipulationDocument,
    uploadStipulationDocument,
    deleteStipulationDocument,
  } = useCentralDocumentHandling(id, sourceType, entityType);

  const [showOnfidoPopup, setShowOnfidoPopup] = useState(false);
  const [onfidoCustomerRole, setOnfidoCustomerRole] = useState<string>(ApplicantType.APPLICANT);
  const [onfidoIsSignatory, setOnfidoIsSignatory] = useState(false);
  const [onfidoLoading, setOnfidoLoading] = useState(false);
  const [isWaitingForStipulationUpdate, setIsWaitingForStipulationUpdate] = useState(false);

  const handleOnfidoClick = useCallback((customerRole: string, stipulation: StipulationItem) => {
    const signatory = isSignatoryIdCardStipulation(stipulation);
    setOnfidoIsSignatory(signatory);
    // Signatory uses COAPPLICANT role at the backend regardless of which UI section renders the stipulation.
    setOnfidoCustomerRole(signatory ? ApplicantType.COAPPLICANT : customerRole);
    setShowOnfidoPopup(true);
  }, []);

  const handleOnfidoConfirm = useCallback(async () => {
    setShowOnfidoPopup(false);
    setOnfidoLoading(true);
    try {
      const currentUrl = new URL(window.location.href);
      const pathOnly = currentUrl.pathname;
      const isSignatory = onfidoIsSignatory;
      const isCoCustomer = onfidoCustomerRole === ApplicantType.COAPPLICANT;

      const creditApplicationId = await getCreditApplicationId(id);
      if (!creditApplicationId) {
        throw new Error(`No credit application found for offer ${id}`);
      }

      const [applicantCreditScoreId, applicantCreditScoreIdCoCustomer] = await Promise.all([
        getCustomerCreditScoreId(creditApplicationId),
        isCoCustomer && !isSignatory ? getCoCustomerCreditScoreId(creditApplicationId) : Promise.resolve(undefined),
      ]);

      const targetCreditScoreId =
        isCoCustomer && !isSignatory ? applicantCreditScoreIdCoCustomer : applicantCreditScoreId;
      if (!targetCreditScoreId || !applicantCreditScoreId) {
        throw new Error(`No applicant credit score ID found for role ${onfidoCustomerRole}`);
      }

      const { customerData } = await getCreditApplicationCustomerData(id, creditApplicationId, targetCreditScoreId);

      let stakeholderContacts: QuoteSchema.Schemas.StakeholderContact[] | undefined;
      if (customerData.customerType !== 'INDIVIDUAL') {
        const response = await getQuoteStakeholderContacts(id);
        stakeholderContacts = response.stakeholderContacts;
      }

      const identData = extractDashboardIdentData(customerData, stakeholderContacts);

      const { dataStorageId } = await prepareDashboardIdent({
        offerId: id,
        customerRole: onfidoCustomerRole,
        identData,
        creditApplicationId,
        applicantCreditScoreId,
        applicantCreditScoreIdCoCustomer,
        isSignatory,
      });
      storage.dashboardOnfidoFlow.postOnfidoDashboardRedirect = pathOnly;
      const { redirectUrl } = await startDashboardIdent(dataStorageId, onfidoCustomerRole);
      window.open(redirectUrl, '_self', 'rel=noopener noreferrer');
    } catch {
      setOnfidoLoading(false);
    }
  }, [id, onfidoCustomerRole, onfidoIsSignatory]);

  const handleOnfidoCancel = useCallback(() => {
    setShowOnfidoPopup(false);
  }, []);

  const headingTitle = useMemo(() => {
    switch (entityType) {
      case DashboardSubpageDocumentsEntityType.CUSTOMER_DOCUMENTS:
        return t('dashboard:centralDocumentHandling:titleCustomer');
      case DashboardSubpageDocumentsEntityType.DEALER_DOCUMENTS:
        return t('dashboard:centralDocumentHandling:titleDealer');
      default:
        return '';
    }
  }, [entityType]);
  const onDownloadButton = useCallback(
    async (stipulationId: string, documentId: string, documentName: string, coApplicantId?: string) => {
      await getStipulationDocument(stipulationId, documentId, documentName, coApplicantId);
    },
    [getStipulationDocument]
  );

  const onDeleteButton = useCallback(
    async (stipulationId: string, documentId: string, coApplicantId?: string) => {
      await deleteStipulationDocument(stipulationId, documentId, coApplicantId);
    },
    [deleteStipulationDocument]
  );

  const onUploadInputChange = useCallback(
    async (event: BaseSyntheticEvent, stipulationId: string, coApplicantId?: string, stipulationStatus?: string) => {
      const file = event.target.files[0];

      if (file) {
        await uploadStipulationDocument(stipulationId, file, coApplicantId);

        // Only refresh list when uploading new files in place of refused ones
        if (stipulationStatus === MilesCreditApplicationStipulationStatus.REFUSED) {
          setIsWaitingForStipulationUpdate(true);
          await waitForMiles();
          await getStipulationList();
          setIsWaitingForStipulationUpdate(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadStipulationDocument]
  );

  useEffect(() => {
    const getStipulationListRequest = async () => {
      await getStipulationList();
    };

    getStipulationListRequest();
  }, [getStipulationList, sourceType]);

  const globalLoading = useSelector(loadingSelectors.selectLoading);
  useRemoveCardTitle(globalLoading);

  const renderDeleteButton = useCallback(
    (stipulationId, document, coApplicantId?: string) => {
      if (sourceType === DashboardSubpageDocumentsSourceType.RUNNING) {
        return null;
      }

      return loadingId === `document-delete-${document.documentId}` ? (
        <Spinner small className="u-float-right u-mr" />
      ) : (
        <Button
          onClick={() => onDeleteButton(stipulationId, document.documentId, coApplicantId)}
          icon="semantic-delete"
          link
          simple
          small
          ariaLabel={`${t('dashboard:centralDocumentHandling:stipulations:buttonsLabels:delete')} ${document.name}, id: ${document.documentId}`}
          disabled={false}
        />
      );
    },
    [loadingId, onDeleteButton, sourceType]
  );

  const renderDownloadButton = useCallback(
    (stipulationId: string, document, coApplicantId?: string) => {
      return loadingId === `document-get-${document.documentId}` ? (
        <Spinner small className="u-float-right u-mr" />
      ) : (
        <Button
          onClick={() => onDownloadButton(stipulationId, document.documentId, document.description, coApplicantId)}
          icon="semantic-download"
          link
          simple
          small
          ariaLabel={`${t('dashboard:centralDocumentHandling:stipulations:buttonsLabels:download')} ${document.name}, id: ${document.documentId}`}
          disabled={false}
        />
      );
    },
    [loadingId, onDownloadButton]
  );
  const renderStipulationAction = useCallback(
    (stipulation: StipulationItem, customerRole: string, coApplicantId?: string) => {
      if (isOnfidoRequired(stipulation)) {
        return (
          <Layout flush right>
            <Layout.Item default="1/4" s="1/2" className="u-text-right">
              {onfidoLoading ? (
                <Spinner small className="u-float-right u-mr" />
              ) : (
                <Button
                  onClick={() => handleOnfidoClick(customerRole, stipulation)}
                  icon="semantic-edit"
                  small
                  link
                  simple
                  ariaLabel={t('dashboard:centralDocumentHandling:stipulations:onfidoButtonLabel')}
                >
                  {t('dashboard:centralDocumentHandling:stipulations:onfidoButtonLabel')}
                </Button>
              )}
            </Layout.Item>
          </Layout>
        );
      }

      if (stipulation.canUpload) {
        return (
          <Layout flush right>
            <Layout.Item default="1/4" s="1/2" className="u-text-right">
              {loadingId === `document-upload-${stipulation.id}` ? (
                <Spinner small className="u-float-right u-mr" />
              ) : (
                <Button
                  onClick={() => uploadInputs.current[stipulation.id].click()}
                  icon="semantic-save-cloud"
                  small
                  link
                  simple
                  ariaLabel={`${t('dashboard:centralDocumentHandling:stipulations:buttonsLabels:upload')} ${stipulation.id}`}
                  disabled={false}
                >
                  {t('dashboard:popup:documentUploadButtonTitle')}
                </Button>
              )}
              <input
                ref={(element) => {
                  uploadInputs.current[stipulation.id] = element;
                }}
                onChange={(event) => {
                  onUploadInputChange(event, stipulation.id, coApplicantId, stipulation.status);
                }}
                style={{ zIndex: -5 }}
                className="c-upload__input"
                type="file"
              />
            </Layout.Item>
          </Layout>
        );
      }

      return null;
    },
    [handleOnfidoClick, loadingId, onfidoLoading, onUploadInputChange]
  );

  if (loadingId === 'stipulations-list' || isWaitingForStipulationUpdate) {
    return (
      <Card title="" className="u-mt u-p-small u-text-left">
        <Layout center>
          <Layout.Item>
            <Spinner center />
          </Layout.Item>
        </Layout>
      </Card>
    );
  }

  if (stipulations.length === 0) {
    return (
      <Layout center>
        <Layout.Item className="u-text-center">
          <Heading level={5}>{t('dashboard:centralDocumentHandling:stipulations:noStipulations')}</Heading>
        </Layout.Item>
      </Layout>
    );
  }

  return (
    <>
      <Card title="" className="u-mt u-p-small u-text-left u-text-wrap">
        <Layout>
          <Layout.Item default="1/1">
            <Heading level={5}>{headingTitle}</Heading>
          </Layout.Item>
          <Layout.Item default="1/1">
            <Layout center>
              {stipulations.map((stipulation) => (
                <Layout.Item key={`stipulation-${stipulation.id}`}>
                  <Card title="">
                    <Layout>
                      <Layout.Item default="1/12" s="1/1">
                        <Icon name="semantic-file" size={IconSize.XXLARGE} />
                      </Layout.Item>
                      <Layout.Item default="7/12" s="1/1">
                        <Heading level={6} className="u-text-left u-ml">
                          {stipulation.stipulationTypeTranslation || stipulation.name}
                        </Heading>
                        <DescriptionList table className="u-text-left u-ml">
                          {stipulation.documents.map((document) => (
                            <DescriptionList.Group
                              key={`document-${document.documentId}`}
                              termText={document.description}
                            >
                              <DescriptionList.Detail>
                                <ButtonContainer nav>
                                  {renderDownloadButton(stipulation.id, document)}
                                  {stipulation.canDelete ? renderDeleteButton(stipulation.id, document) : null}
                                </ButtonContainer>
                              </DescriptionList.Detail>
                            </DescriptionList.Group>
                          ))}
                        </DescriptionList>
                      </Layout.Item>
                      <Layout.Item default="4/12" s="1/1">
                        <Layout right className="row">
                          <Layout.Item className="u-text-right">
                            <Tag
                              success={stipulation.status === MilesCreditApplicationStipulationStatus.ACTIVE}
                              error={
                                stipulation.status === MilesCreditApplicationStipulationStatus.REFUSED ||
                                stipulation.status === MilesCreditApplicationStipulationStatus.CANCELED
                              }
                              warning={stipulation.status === MilesCreditApplicationStipulationStatus.RECEIVED}
                            >
                              {t(
                                `dashboard:centralDocumentHandling:stipulations:stipulationStatus:${stipulation.status}`
                              )}
                            </Tag>
                            <Tag className="u-ml">
                              {t(
                                `dashboard:centralDocumentHandling:stipulations:${stipulation.isMandatory ? 'mandatoryText' : 'optionalText'}`
                              )}
                            </Tag>
                          </Layout.Item>
                        </Layout>
                      </Layout.Item>
                    </Layout>
                    {renderStipulationAction(stipulation, ApplicantType.APPLICANT)}
                  </Card>
                </Layout.Item>
              ))}
            </Layout>
          </Layout.Item>
        </Layout>
      </Card>
      {!!creditApplicationCoApplicantId && (
        <Card title="" className="u-mt u-p-small u-text-left">
          <Layout>
            <Layout.Item default="1/1">
              <Heading level={5}>{t('dashboard:centralDocumentHandling:titleCoCustomer')}</Heading>
            </Layout.Item>
            <Layout.Item default="1/1">
              <Layout center>
                {coApplicantStipulations.map((stipulation) => (
                  <Layout.Item key={`stipulation-${stipulation.id}`}>
                    <Card title="">
                      <Layout>
                        <Layout.Item default="1/12" s="1/1">
                          <Icon name="semantic-file" size={IconSize.XXLARGE} />
                        </Layout.Item>
                        <Layout.Item default="7/12" s="1/1">
                          <Heading level={6} className="u-text-left">
                            {stipulation.stipulationTypeTranslation || stipulation.name}
                          </Heading>
                          <DescriptionList table>
                            {stipulation.documents.map((document) => (
                              <DescriptionList.Group
                                key={`document-${document.documentId}`}
                                termText={document.description}
                              >
                                <DescriptionList.Detail>
                                  <ButtonContainer nav>
                                    {renderDownloadButton(stipulation.id, document, creditApplicationCoApplicantId)}
                                    {stipulation.canDelete
                                      ? renderDeleteButton(stipulation.id, document, creditApplicationCoApplicantId)
                                      : null}
                                  </ButtonContainer>
                                </DescriptionList.Detail>
                              </DescriptionList.Group>
                            ))}
                          </DescriptionList>
                        </Layout.Item>
                        <Layout.Item default="4/12" s="1/1">
                          <Layout right className="row">
                            <Layout.Item className="u-text-right">
                              <Tag
                                success={stipulation.status === MilesCreditApplicationStipulationStatus.ACTIVE}
                                error={
                                  stipulation.status === MilesCreditApplicationStipulationStatus.REFUSED ||
                                  stipulation.status === MilesCreditApplicationStipulationStatus.CANCELED
                                }
                                warning={stipulation.status === MilesCreditApplicationStipulationStatus.RECEIVED}
                              >
                                {t(
                                  `dashboard:centralDocumentHandling:stipulations:stipulationStatus:${stipulation.status}`
                                )}
                              </Tag>
                              <Tag className="u-ml">
                                {t(
                                  `dashboard:centralDocumentHandling:stipulations:${stipulation.isMandatory ? 'mandatoryText' : 'optionalText'}`
                                )}
                              </Tag>
                            </Layout.Item>
                          </Layout>
                        </Layout.Item>
                      </Layout>
                      {renderStipulationAction(stipulation, ApplicantType.COAPPLICANT, creditApplicationCoApplicantId)}
                    </Card>
                  </Layout.Item>
                ))}
              </Layout>
            </Layout.Item>
          </Layout>
        </Card>
      )}
      {showOnfidoPopup && (
        <DashboardActionPopup
          handleClose={handleOnfidoCancel}
          onConfirm={handleOnfidoConfirm}
          text={t('dashboard:centralDocumentHandling:stipulations:onfidoConfirmationText')}
        />
      )}
    </>
  );
};
